// Portrait.jsx
import React, { useRef, useState, useEffect } from 'react'
import * as THREE from 'three'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import JoImageSrc from '/assets/images/JoTRYagainkopie.jpg'
import gsap from 'gsap'
import { Billboard } from '@react-three/drei'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import { Volume2, VolumeX, Play, Pause } from "lucide-react";

import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button';
import { Separator } from "@/components/ui/separator"


const randomOrder = [
  82, 128, 4, 0, 7, 109, 22, 90, 67, 41, 101, 78, 104, 88, 3, 9, 79, 76, 37, 57, 14, 35, 74, 43, 21, 124, 117, 92, 96, 62,
  89, 17, 68, 52, 87, 73, 26, 95, 66, 97, 118, 71, 111, 123, 93, 58, 127, 129, 65, 114, 69, 106, 105, 53, 36, 48, 99, 51, 47, 19,
  8, 46, 23, 98, 20, 2, 1, 119, 64, 10, 25, 63, 27, 49, 38, 133, 112, 13, 134, 44, 75, 54, 29, 59, 33, 5, 6, 107, 50, 84,
  115, 80, 28, 60, 116, 32, 130, 11, 125, 72, 94, 121, 102, 15, 126, 81, 85, 83, 122, 42, 24, 70, 108, 131, 31, 86, 55, 120, 61, 56,
  40, 132, 18, 30, 45, 12, 91, 34, 100, 39, 16, 103, 77, 110, 113
];

const mySpeed = 6000

// colours
let pink_colour = '#ff0086'
let blue_colour = '#00f3ff'
let green_colour = '#00ff81'
let purple_colour = '#cf69ff'
let yellow_colour = '#f7f000'

let pink_background = '#f9c1da'
let blue_background = '#d3fafe'
let green_background = '#d5fedd'
let purple_background = '#f6d1ff'
let yellow_background = '#fcfaca'

const locationColors = {
  "Zuid-Oost": blue_colour,
  "Oost": purple_colour,
  "Noord": yellow_colour,
  "West": green_colour,
  "Nieuw-West": pink_colour,
  "All": '#ddd'
};

// Instead of a simple array of image URLs, store your artworks as objects:
//

/* -----------------------------------------------------------
   Particle Component
   - Renders an individual particle (sphere)
   - When clicked, calls onSelect with its artwork object.
   - Its material color changes based on selection or if its artwork’s location matches the selected location.
----------------------------------------------------------- */
const Particle = ({
  position,
  size,
  artwork,
  onSelect,
  selected,
  baseColor,
  selectedLocation,
  delay = 0,
  resetCounter,
}) => {
  const groupRef = useRef()
  const meshRef  = useRef()
  const pulseRef = useRef()

  // pulsing group
  useEffect(() => {
    if (!groupRef.current) return
    const tween = gsap.to(groupRef.current.scale, {
      x: 2.2, y: 2.2, z: 2.2,
      duration: 0.5, ease: 'power1.in',
      delay, yoyo: true, repeat: -1
    })
    return () => tween.kill()
  }, [delay])

  // “pop” on select
  useEffect(() => {
    if (selected && groupRef.current) {
      gsap.fromTo(
        groupRef.current.scale,
        { x:1, y:1, z:1 },
        { x:9, y:9, z:9, duration:1.6, ease:'power1.out', yoyo:true, repeat:3 }
      )
    }
  }, [selected])

 useEffect(() => {
   if (!groupRef.current || !pulseRef.current) return
   gsap.killTweensOf(groupRef.current.scale)
  groupRef.current.scale.set(1, 1, 1)
  pulseRef.current.restart(true)
 }, [resetCounter])

  // sphere color = location match ? locationColors[...] : baseColor
  useEffect(() => {
    if (!meshRef.current) return
    const mat = meshRef.current.material
    const useColor = selectedLocation && artwork.location === selectedLocation
      ? locationColors[artwork.location] || baseColor
      : baseColor
    const target = new THREE.Color(useColor)
    gsap.to(mat.color, {
      r: target.r, g: target.g, b: target.b,
      duration: 0.8, ease: 'power1.out'
    })
  }, [selectedLocation, artwork.location, baseColor])

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setDetailArtwork(null)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])
  
  return (
    <group
      ref={groupRef}
      position={position}
      onClick={e => { e.stopPropagation(); onSelect(artwork) }}
      scale={[1,1,1]}
    >
      {/* the sphere itself */}
      <mesh ref={meshRef}>
        <sphereGeometry args={[size, 4, 4]} />
        <meshBasicMaterial transparent opacity={0.9} />
      </mesh>

      {/* only draw an aqua ring on the selected one */}
      {selected && (
        <Billboard>
          <mesh /* renderOrder={999} */        
          >
            <ringGeometry args={[ size + 0.2, size + 3.25, 32 ]} />
            <meshBasicMaterial
              color={locationColors[artwork.location]}
              transparent
              opacity={0.6}
              side={THREE.DoubleSide}
              //depthTest={false}
            />
          </mesh>
        </Billboard>
      )}
    </group>
  )
}

/* -----------------------------------------------------------
   Particles Component
   - Loads the mapping image and extracts pixel data offscreen.
   - Creates particle positions based on pixel brightness.
   - For each particle, instead of randomly assigning an image, it assigns an artwork from the artworks array.
   - The assignment is done deterministically (cycling through artworks).
----------------------------------------------------------- */
const Particles = ({ artworks, onSelect, selectedIndex, selectedLocation, resetCounter }) => {
  const [particles, setParticles] = useState([])
  const [particlesLoaded, setParticlesLoaded] = useState(false)
  const mappingImageSrc = JoImageSrc

  useEffect(() => {
    const image = new Image()
    image.src = mappingImageSrc
    image.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = image.width
      canvas.height = image.height
      const context = canvas.getContext('2d')
      context.drawImage(image, 0, 0)
      const imageData = context.getImageData(0, 0, image.width, image.height).data

      const tempParticles = []
      let counter = 0


      for (let y = 0; y < image.height; y++) {
        for (let x = 0; x < image.width; x++) {
          const idx = (x + y * image.width) * 4
          const r = imageData[idx]
          const g = imageData[idx + 1]
          const b = imageData[idx + 2]
          const a = imageData[idx + 3]

          // Skip transparent and white/near-white pixels
          if (a === 0 || (r === 255 && g === 255 && b === 255)) continue
          const threshold = 180
          if (r > threshold && g > threshold && b > threshold) continue

          const brightness = (r + g + b) / 3
          const zMin = 60, zMax = -60
          const z = THREE.MathUtils.mapLinear(brightness, 0, 255, zMin, zMax)
          const xOffset = x - image.width / 2
          const yOffset = y - image.height / 2

          // Deterministically assign an artwork by cycling through the artworks array:
          const artwork = artworks[counter % artworks.length]
          counter++


          // Create a color from the pixel values (normalized to [0,1])
          const pixelColor = new THREE.Color(r / 255, g / 255, b / 255)

          tempParticles.push({ position: [xOffset, yOffset, z], artwork, color: pixelColor })
        }
      }
      setParticles(tempParticles)
      setParticlesLoaded(true)
    }
    image.onerror = (err) => console.error("Mapping image failed to load:", err)
  }, [mappingImageSrc])

  const minSize = 0.1, maxSize = 0.6

  if (!particlesLoaded) {
    return null // or a loading indicator if you prefer
  }

  return (
    <>
      {particles.map((p, index) => {
        const size = THREE.MathUtils.mapLinear(p.position[2], -60, 60, minSize, maxSize)
        const shake = 0
        const pos = [
          p.position[0] + shake,
          p.position[1] + shake,
          p.position[2] + shake
        ]

        return (
          <Particle
            key={index}
            position={pos}
            size={size}
            artwork={p.artwork}
            onSelect={() => {
              onSelect(p.artwork, index)    // ◀ pass both art & idx back up
            }}
            selected={selectedIndex === index}
            resetCounter={resetCounter}
            baseColor={p.color}
            selectedLocation={selectedLocation}
            delay={(index % 100) * 0.05}
          />
        )

      })}
    </>
  )
}

/* -----------------------------------------------------------
   Portrait Component
   - Sets up the Three.js scene using react-three-fiber's Canvas.
   - Maintains state for the selected artwork and the selected area (location).
   - Renders an overlay with details that update dynamically based on the selected artwork.
   - Also renders a select component that lets you choose a location, which affects particle colors.
   - Adds arrow buttons (back and forward) in the text container to browse artworks.
----------------------------------------------------------- */
const Portrait = () => {
  const [selectedArtwork, setSelectedArtwork] = useState(null)
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [artworks, setArtworks] = useState([])
  const [resetCounter, setResetCounter] = useState(0)
  const [viewMode, setViewMode] = useState('world') // 'world' | 'list'
  const [detailArtwork, setDetailArtwork] = useState(null) // for list image zoom

  const audioRef = useRef(null);
const intervalRef = useRef(null);

const [isAudioPlaying, setIsAudioPlaying] = useState(false);
const [isLoopPlaying, setIsLoopPlaying] = useState(true); // B: data loop play/pause

const toggleAudio = async () => {
  const el = audioRef.current;
  if (!el) return;

  if (isAudioPlaying) {
    el.pause();
    setIsAudioPlaying(false);
  } else {
    try {
      await el.play();
      setIsAudioPlaying(true);
    } catch (e) {
      console.warn("Audio playback blocked until user interacts:", e);
    }
  }
};

const toggleLoop = () => {
  setIsLoopPlaying((v) => !v);
};

useEffect(() => {
  const el = audioRef.current;
  if (!el) return;
  const onEnded = () => setIsAudioPlaying(false);
  el.addEventListener("ended", onEnded);
  return () => el.removeEventListener("ended", onEnded);
}, []);


const advanceTo = (idx) => {
  const art = artworks[idx];
  if (!art) return;

  setSelectedIndex(idx);
  setSelectedArtwork(art);
  setSelectedLocation(art.location);
  setResetCounter((c) => c + 1);
};

// Start on first item when artworks load
useEffect(() => {
  if (!artworks.length) return;
  advanceTo(0);
}, [artworks]);

// Loop controller
useEffect(() => {
  if (!artworks.length) return;

  // always clear previous interval
  if (intervalRef.current) {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }

  if (!isLoopPlaying) {
    // paused: do nothing
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }

  intervalRef.current = setInterval(() => {
    setSelectedIndex((prev) => {
      const current = prev ?? 0;
      const next = (current + 1) % artworks.length;

      const art = artworks[next];
      if (art) {
        setSelectedArtwork(art);
        setSelectedLocation(art.location);
        setResetCounter((c) => c + 1);
      }
      return next;
    });
  }, mySpeed);

  return () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };
}, [artworks, isLoopPlaying]);

  // 1) fetch your JSON on mount
  useEffect(() => {
    fetch('/assets/data/artworks_final.json')
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((json) => {
        // build a new array in your exact `randomOrder`
        const ordered = randomOrder.map(i => json[i])
        setArtworks(ordered)
      })    
      .catch((err) => console.error('Failed loading artworks:', err))
  }, [])
  

// 3) now you can early-return your loading state
if (artworks.length === 0) {
  return <div>Loading artwork data…</div>
}


  // Define background colors for the Three.js scene
  const backgroundColors = {
    "Zuid-Oost": blue_background,
    "Oost": purple_background, 
    "Noord": yellow_background, 
    "Nieuw-West": pink_background, 
    "West": green_background, 
    "All": '#ddd', 
  };

  // const backgroundColors = {
  //   zuid_oost: '#ffd9c0', //'#d8f842', // red
  //   east: '#ffeec0',  // yellow
  //   south: '#ddd', // blue
  //   west: '#fbc0ff',  // green
  //   all: '#ddd',  // green
  // };

  // Handlers for arrow navigation:
  const handlePrev = () => {
    if (selectedIndex == null) return;
    const newIdx = (selectedIndex - 1 + artworks.length) % artworks.length;
    const art = artworks[newIdx];
    setSelectedIndex(newIdx);
    setSelectedArtwork(art);
    setSelectedLocation(art.location);       // ← sync location
    setResetCounter((c) => c + 1);
    setIsLoopPlaying(false);


  };

  const handleNext = () => {
    if (selectedIndex == null) return;
    const newIdx = (selectedIndex + 1) % artworks.length;
    setSelectedIndex(newIdx);
    const art = artworks[newIdx];
    setSelectedArtwork(art);
    setSelectedLocation(art.location);       // ← sync location
    setResetCounter((c) => c + 1);
    setIsLoopPlaying(false);


  };


  return (
    <>

{/* Audio */}
<audio ref={audioRef} src="/assets/audio/jo_soundscape.wav" preload="auto" />

{/* GO BACK */}
{viewMode === 'world' && (
 <div className="fixed top-0 left-5 z-50 mt-4 flex items-start justify-left space-x-4 ">
        <Link to="/nl">
    <Button
      variant="link"
      className="px-5 py-6 text-left text-white border-2 border-black bg-black text-lg rounded-none focus:outline-none focus:ring-0"
    >
      ←
    </Button>
  </Link>
      </div>
            )}

      {/* BACK TO WORLD */}
      {viewMode === 'list' && (

 <div className="fixed top-0 left-5 z-50 mt-4 flex items-start justify-left space-x-4 ">

    <Button
          variant="link"
          onClick={() => setViewMode('world')}
      className="px-5 py-6 text-left text-white border-2 border-black bg-black text-lg rounded-none focus:outline-none focus:ring-0"
    >
      ←
    </Button>
      </div>
      )}


      {/* VIEW TOGGLE */}
<div className="fixed top-0 right-5 z-50 mt-4 flex items-start space-x-2">

<div className=" focus:outline-none focus:ring-0 transform z-50 pointer-events-auto flex items-center space-x-4 bg-[#ddd]/0 rounded-none">
        {/* <p className="text-black text-5xl  font-gotham font-medium">Location</p> */}
        <Select onValueChange={(value) => setSelectedLocation(value)}>
          <SelectTrigger className="w-auto p-0 px-4 md:px-24 py-6 bg-black border-2 border-black text-lg focus:outline-none focus:ring-0 rounded-none">
            <SelectValue placeholder="Locaties" />
          </SelectTrigger>
          <SelectContent className="bg-black/80 border-none text-lg text-white p-4">
          <SelectItem value="All" className="text-lg p-4">Toon alle</SelectItem>
            <SelectItem value="Noord" className="text-lg p-4">Noord</SelectItem>
            <SelectItem value="Oost" className="text-lg p-4">Oost</SelectItem>
            <SelectItem value="Zuid-Oost" className="text-lg p-4">Zuid-Oost</SelectItem>
            <SelectItem value="West" className="text-lg p-4">West</SelectItem>
            <SelectItem value="Nieuw-West" className="text-lg p-4">Nieuw-West</SelectItem>
          </SelectContent>
        </Select>
      </div> 
  <Button
    variant="link"
    onClick={() => setViewMode('world')}
    className={
      "px-3 text-lg rounded-none focus:outline-none focus:ring-0 border-2 py-6 " +
      (viewMode === 'world'
        ? "bg-black text-white border-black"
        : "bg-transparent text-black border-black")
    }
  >
    Portret
  </Button>

  <Button
    variant="link"
    onClick={() => setViewMode('list')}
    className={
      "px-5 text-lg rounded-none focus:outline-none focus:ring-0 border-2 py-6 " +
      (viewMode === 'list'
        ? "bg-black text-white border-black"
        : "bg-transparent text-black border-black")
    }
  >
    Lijst
  </Button>
</div>

{/* BG Balk */}
{viewMode === 'list' && (
<div
className='bg-background h-24 fixed z-[49] top-0 w-full'></div>
)}

{/* !!! WORLD VIEW */}
{/* THREE CANVAS */}
{viewMode === 'world' && (
  <Canvas
    dpr={[1, window.devicePixelRatio]}
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: selectedLocation ? backgroundColors[selectedLocation] : '#ddd',
      transition: 'background 1.8s ease-in-out',
    }}
    camera={{ position: [0, 0, 1300], near: 0.1, far: 5000 }}
  >
    <group position={[0, 0, 0]} scale={[8, 8, 8]} rotation={[0, Math.PI, Math.PI]}>
      <Particles
        artworks={artworks}
        onSelect={(art, idx) => {
          setSelectedArtwork(art)
          setSelectedLocation(art.location)
          setSelectedIndex(idx)
        }}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        selectedLocation={selectedLocation}
      />
    </group>
    <OrbitControls
      autoRotate
      autoRotateSpeed={2.8}
      enableDamping
      dampingFactor={0.05}
    />
  </Canvas>
)}

{/* LOCATION SELECTOR */}
{/* {viewMode === 'world' && (
      <div className="fixed bottom-11 focus:outline-none focus:ring-0 right-5 transform z-50 pointer-events-auto flex items-center space-x-4 bg-[#ddd]/0 py-8 rounded-3xl">
        <Select onValueChange={(value) => setSelectedLocation(value)}>
          <SelectTrigger className="w-auto p-8 px-24 bg-black border-none text-lg focus:outline-none focus:ring-0">
            <SelectValue placeholder="Locations" />
          </SelectTrigger>
          <SelectContent className="bg-black/80 border-none text-lg text-white p-4">
          <SelectItem value="All" className="text-lg p-4">Show all</SelectItem>
            <SelectItem value="Noord" className="text-lg p-4">Noord</SelectItem>
            <SelectItem value="Oost" className="text-lg p-4">Oost</SelectItem>
            <SelectItem value="Zuid-Oost" className="text-lg p-4">Zuid-Oost</SelectItem>
            <SelectItem value="West" className="text-lg p-4">West</SelectItem>
            <SelectItem value="Nieuw-West" className="text-lg p-4">Nieuw-West</SelectItem>
          </SelectContent>
        </Select>
      </div>
)} */}

       {/* BUTTONS */}
{/* {viewMode === 'world' && (
 <div className="fixed bottom-0 left-0 right-0 z-30 mt-4 flex items-center justify-center space-x-4 bg-black">
        <button
          onClick={handlePrev}
          className="px-8 bg-black text-white p-4 hover:bg-[#232323] rounded-none focus:outline-none focus:ring-0"
        >
          ←
        </button>
        <p className="text-md mt-2 text-white font-gotham">
          {selectedIndex + 1} / {artworks.length}
        </p>
        <button
          onClick={handleNext}
          className="px-8 bg-black text-white p-4 hover:bg-[#232323] rounded-none focus:outline-none focus:ring-0"
        >
          →
        </button>
      </div>
)} */}

{/* BOTTOM BAR */}
{viewMode === 'world' && (
  <div className="fixed bottom-0 left-0 right-0 z-30 grid grid-cols-[auto_1fr_auto] items-center bg-black">
    
    {/* LEFT: Audio */}
    <div className="flex items-center h-16">
      <button
        onClick={toggleAudio}
        className="px-6 bg-black text-white p-4 hover:bg-[#232323] focus:outline-none focus:ring-0"
      >
        {isAudioPlaying ? <Volume2 size={22} /> : <VolumeX size={22} />}
      </button>
    </div>

    {/* CENTER: arrows + play */}
    <div className="flex items-center justify-center space-x-2 md:space-x-4">
      <button
        onClick={handlePrev}
        className="px-4 md:px-8 bg-black text-white p-4 hover:bg-[#232323] focus:outline-none focus:ring-0"
      >
        ←
      </button>

      <button
        onClick={toggleLoop}
        className="px-6 bg-black text-white p-4 hover:bg-[#232323] focus:outline-none focus:ring-0"
      >
        {isLoopPlaying ? <Pause size={22} /> : <Play size={22} />}
      </button>

      <button
        onClick={handleNext}
        className="px-4 md:px-8 bg-black text-white p-4 hover:bg-[#232323] focus:outline-none focus:ring-0"
      >
        →
      </button>
    </div>

    <div className="px-5 text-white font-gotham min-w-fit whitespace-nowrap tabular-nums">
  {(selectedIndex ?? 0) + 1} / {artworks.length}
</div>



  </div>
)}



      {/* ARTWORKS & TITLECARDS */}
{viewMode === "world" && selectedArtwork && (
  <div
    className="
      fixed z-20 pointer-events-none opacity-95
      left-0 right-0 bottom-20 px-4
      md:left-20 md:right-auto md:bottom-1/2 md:translate-y-1/2 md:px-0
    "
  >
    <div
      className="
        pointer-events-auto
        w-full max-w-xl mx-auto
        rounded-none
        bg-black/95 text-white

        p-4 sm:p-6
        flex gap-4 items-center
        md:bg-transparent md:text-black md:p-0 md:block md:max-w-none md:w-auto
      "
    >
      {/* IMAGE */}
      <div
        className="
          w-36 aspect-square flex-shrink-0
          bg-[#232323] md:rounded-3xl overflow-hidden
          md:w-80
        "
      >
        <img
          src={`/assets/artworks/${selectedArtwork.image}`}
          alt={selectedArtwork.artist}
          className="w-full h-full object-contain"
        />
      </div>

      {/* TEXT */}
      <div className="text-left font-gotham md:max-w-80 md:px-2">
        <p className="text-base sm:text-lg md:text-2xl font-bold mb-2 md:mb-4 md:mt-8">
          {selectedArtwork.artist}
        </p>

        <p className="text-xs sm:text-sm md:text-md">
          Amsterdam {selectedArtwork.location}, {selectedArtwork.year}
        </p>

        <p className="text-xs sm:text-sm md:text-md">
          {selectedArtwork.material_NL}
        </p>

        <p className="text-xs sm:text-sm md:text-md mt-2 md:mt-4">
          Dit werk is gemaakt bij {selectedArtwork.organisation} onder begeleiding van{" "}
          {selectedArtwork.coordinator}
        </p>
      </div>
    </div>
  </div>
)}



{/* !!! LIST VIEW */}
{viewMode === 'list' && (
  <div className="fixed inset-0 z-40 bg-[#232323] text-background overflow-y-auto">
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">

      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl md:text-3xl font-gotham font-bold mt-48">
          Alle werken ({artworks.length})
        </h1>
      </div>

      {/* Header row */}
      <div className="hidden md:flex text-xs font-gotham uppercase tracking-wide text-background mb-3">
        <div className="w-[12.5rem]">Afbeelding</div>
        <div className="flex-[0.77]">Kunstenaar</div>
        <div className="flex-[0.92]">Materiaal / techniek</div>
        <div className="flex-[1]">Details</div>
      </div>
      <Separator className="my-4 bg-gray-600 h-0.5" />

      {/* Rows */}
      {artworks.map((art, index) => (
  <div key={index}>
    <div className="flex flex-col md:flex-row gap-4 md:gap-6 py-4 items-start">

{/* image */}
<div
  className="w-44 h-44 bg-[#232323] rounded-xl overflow-hidden flex-shrink-0 cursor-zoom-in"
  onClick={() => setDetailArtwork(art)}
>
  <img
    src={`/assets/artworks/${art.image}`}
    alt={art.artist}
    className="w-full h-full object-contain"
  />
</div>



      {/* 2. Kunstenaar */}
      <div className="md:flex-1 font-gotham font-semibold text-lg md:text-3xl">
        {art.artist}
      </div>

      {/* 3. Materiaal / techniek */}
      <div className="md:flex-[1.2] text-background text-sm md:text-sm">
        {art.material_NL}
      </div>

      {/* 4. Details */}
      <div className="md:flex-[1.4] text-background text-xs md:text-sm space-y-1">
        <p>Locatie: {art.location}</p>
        <p>Jaar: {art.year}</p>
        <p>Organisatie: {art.organisation}</p>
        {art.coordinator && (
          <p>Begeleiding: {art.coordinator}</p>
        )}
      </div>
    </div>

    <Separator className="my-2 bg-gray-600 h-0.5" />
  </div>
))}

    </div>
  </div>
)}


{/* DETAIL OVERLAY (image zoom) */}
{detailArtwork && (
  <div
    className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center"
    onClick={() => setDetailArtwork(null)} // click background to close
  >
    <div
      className="relative max-w-5xl w-[90vw] max-h-[90vh] pointer-events-auto"
      onClick={(e) => e.stopPropagation()} // keep clicks inside from closing
    >
      {/* Close button */}
      <button
        onClick={() => setDetailArtwork(null)}
        className="absolute top-0 right-0 z-10 text-white text-2xl font-gotham font-bold px-3 py-1 rounded-full bg-black/60 focus:outline-none focus:ring-0"
      >
        Close
      </button>

      {/* Image container */}
      <div className="bg-[#232323] rounded-3xl overflow-hidden p-24 mx-auto h-fit w-fit flex items-center justify-center ">
        <img
          src={`/assets/artworks/${detailArtwork.image}`}
          alt={detailArtwork.artist}
          className="max-w-full object-contain"
        />
      </div>
    </div>
  </div>
)}

    </>
  )
}


export default Portrait
